import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MobileStepper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {GetCustomerPopupsResponse} from '@resurs-internal/rb-app-api';
import {useTranslate} from 'hooks';
import React, {useRef, useState} from 'react';
import {Swiper, SwiperClass, SwiperSlide} from 'swiper/react';
import 'swiper/css';

type Content = NonNullable<
  GetCustomerPopupsResponse['popups'][number]['content']['announcement']
>;

interface Props {
  content: Content;
  onClose: () => void;
  isPending: boolean;
}

export const PopupAnnouncementDialog: React.FC<Props> = ({
  content,
  onClose,
  isPending,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const swiperRef = useRef<{swiper: SwiperClass}>(null);

  const {pages} = content;
  const isLastPage = activeStep === pages.length - 1;

  const handleNext = () => {
    if (isLastPage) {
      onClose();
      return;
    }
    setActiveStep((s) => s + 1);
    swiperRef.current?.swiper.slideNext();
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onClose();
      return;
    }
    setActiveStep((s) => s - 1);
    swiperRef.current?.swiper.slidePrev();
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          background: theme.palette.background.paper,
          maxHeight: {xs: '95%', md: '95%'},
          position: {xs: 'absolute', md: 'relative'},
          borderTopRightRadius: {xs: '20px', md: undefined},
          borderTopLeftRadius: {xs: '20px', md: undefined},
          bottom: 0,
          border: 0,
        },
      }}>
      <DialogContent
        sx={{
          minWidth: {xs: '100%', md: '600px'},
          height: '600px',
          p: 'unset',
          '& .swiper': {height: '100%'},
        }}>
        <Swiper
          ref={swiperRef}
          onSlideChange={(swiper) => setActiveStep(swiper.activeIndex)}
          spaceBetween={0}
          slidesPerView={1}>
          {pages.map((page, index) => (
            <SwiperSlide key={index}>
              <Box
                sx={{
                  display: 'inline-block',
                  height: {md: '50%', xs: '40%'},
                  width: '100%',
                  clipPath: 'ellipse(100% 100% at 50% 0%)',
                }}>
                <Box
                  component="img"
                  src={page.imageUrl}
                  alt={page.title}
                  sx={{objectFit: 'cover', width: '100%', height: '100%'}}
                />
              </Box>
              <Stack p={4} textAlign="center">
                <Typography variant="h2" mb={2}>
                  {page.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={(t) => ({color: t.palette.grey[600]})}>
                  {page.subtitle}
                </Typography>
                {page.deeplink?.webLink && (
                  <Button
                    variant="contained"
                    sx={{minWidth: '200px', alignSelf: 'center', mt: 3}}
                    onClick={() =>
                      window.open(page.deeplink!.webLink, '_blank')
                    }>
                    {page.deeplink.text}
                  </Button>
                )}
              </Stack>
            </SwiperSlide>
          ))}
        </Swiper>
      </DialogContent>
      <MobileStepper
        variant="dots"
        steps={pages.length}
        position="static"
        activeStep={activeStep}
        sx={{pb: 2, justifyContent: 'center'}}
        backButton={undefined}
        nextButton={undefined}
      />
      <DialogActions
        sx={{
          borderTop: '1px solid',
          borderColor: theme.palette.grey[300],
          justifyContent: {xs: 'center', md: 'end'},
        }}>
        <Button
          sx={{display: {xs: 'none', md: 'block'}}}
          color="secondary"
          variant="text"
          onClick={handleBack}
          disabled={isPending}>
          {activeStep === 0
            ? translate('common.skip')
            : translate('common.back')}
        </Button>
        <Button variant="text" onClick={handleNext} disabled={isPending}>
          {isLastPage ? translate('common.done') : translate('common.next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
